/*
* Copyright Gregory Coburn 2020-2026, All Rights Reserved, See license for further details
*/
import { inject, Injectable } from '@angular/core';
import { AbstractHttpService } from '@src/app/shared/abstract-http.service';
import { MailItem, MailStatuses } from '@src/app/model/mailMerge';
import { GridField } from '@src/app/shared/grid/grid-field';
import { Field } from '@src/app/shared/field/Field';
import { MessageService } from '@src/app/shared/message.service';
import { HttpClient, HttpHeaders, HttpParams } from '@angular/common/http';
import { FormTextComponent } from '@src/app/shared/form/form-text/form-text.component';
import { FormButtonComponent } from '@src/app/shared/form/form-button/form-button.component';
import { FormDateTimeComponent } from '@src/app/shared/form/form-date-time/form-date-time.component';
import { FormNumberComponent } from '@src/app/shared/form/form-number/form-number.component';
import { FormPicklistComponent } from '@src/app/shared/form/form-picklist/form-picklist.component';
import { ByteCountPipe } from '@src/app/shared/pipes/byte-count.pipe';
import { AbstractObject } from '@src/app/model/abstract-object';
import { catchError, map } from 'rxjs/operators';
import { of } from 'rxjs';
import { MatDialog } from '@angular/material/dialog';
import { TableDialogComponent } from '@src/app/shared/dialogs/table-dialog/table-dialog.component';
import { FormConfig } from '@src/app/shared/form/FormConfig';
import { FieldSet } from '@src/app/shared/form/field-set/field-set.component';
import { Attachment } from '@src/app/model/attachment';

type GridType = 'recipient'|'model';
@Injectable({
    providedIn: 'root'
})
export class MailService {
    http = inject(HttpClient);
    msgSvc = inject(MessageService);
    dialog = inject(MatDialog);

    getMailItemGrid(dataSvc: AbstractHttpService, type: GridType) {
        return new GridField({
            field: {
                value: 'mailItems', cellOpts: { heading: 'Mailings' },
                formRow: 2, sendServer: false, visible: Field.formOnly,
                tabIcon: 'mail'
            },
            rowFactory: (mi: MailItem) => {
                const opener = () => this.showMail(mi, dataSvc, type);
                return [
                    FormTextComponent.make('From', 'fromAddress', { readonly: true }),
                    FormTextComponent.make('To', 'toAddress', { readonly: true }),
                    FormTextComponent.make('Class', 'class', { readonly: true }),
                    FormButtonComponent.makeTextButton('Subject', 'subject', opener, {
                        calculateValue: (mi: MailItem) => mi.subject?.trim() || $localize `[No Subject]`,
                    }),
                    FormDateTimeComponent.make("Sent", 'createdAt', { sendServer: false, readonly: true }),
                    FormTextComponent.make('Size', 'size', { readonly: true,
                        cellOpts: { width: '20px', style: 'font-size: smaller' },
                        calculateValue: (mi: MailItem) => ByteCountPipe.formatBytes(mi.size)
                    }),
                    FormNumberComponent.make('Status', 'responseCode', {format: 'plain', blankZero: true}, {readonly: true}),
                    FormButtonComponent.makeTextButton('Attached', 'attachments', () => this.showAttachments(mi, dataSvc), {
                        calculateValue: (mi: MailItem) => {
                            const count = mi.attachments as unknown as number;
                            return count > 0 ? String(count) : '';
                        },
                    }),
                    FormPicklistComponent.make('Status Text', 'mailStatus', null, {items: MailStatuses}, {readonly: true}),
                ]
            },
        });
    }

    getMailAttachments(mailItem: MailItem, dataSvc: AbstractHttpService) {
        const url = `${dataSvc.getBaseUrl()}/getMailAttachments/${mailItem.id}`;
        const params = new HttpParams().set('_forceTeam', mailItem.teamId);
        return this.http.get<Attachment[]>(url, { params }).pipe(
            catchError((error) => {
                this.msgSvc.showError(error, 'Retrieving Mail Attachments');
                return of([] as Attachment[]);
            })
        );
    }

    showAttachments(mailItem: MailItem, dataSvc: AbstractHttpService) {
        this.getMailAttachments(mailItem, dataSvc).subscribe(attachments => {
            if (!attachments?.length) return;
            const config = new FormConfig({
                title: $localize`Attachments`,
                fieldSet: new FieldSet({ fields: [
                    FormButtonComponent.makeTableButton('Name', 'name',
                        (item: AbstractObject) => Attachment.download(item as Attachment, dataSvc, mailItem.teamId)
                    ),
                    FormTextComponent.make('Notes', 'notes', { readonly: true }),
                    FormTextComponent.make('Size', 'size', { readonly: true,
                        calculateValue: (a: Attachment) => ByteCountPipe.formatBytes(a.size)
                    }),
                ]}),
                mode: 'list',
            });
            this.dialog.open(TableDialogComponent, { data: { config, items: attachments } });
        });
    }

    getMail(mailItem: MailItem, dataSvc: AbstractHttpService, gridType: GridType) {
        const linkedId = gridType === 'model' ? mailItem.modelId : mailItem.recipientId;
        const url = `${dataSvc.getBaseUrl()}/getMail/${linkedId}/${mailItem.id}`;
        const params = new HttpParams().set('_forceTeam', mailItem.teamId);
        const headers = new HttpHeaders().append('Accept', 'application/text');

        return this.http.get(url, { params, headers, observe: 'response', responseType: 'text' }).pipe(
            map(x => {
                return x;
            }),
            catchError((error) => {
                this.msgSvc.showError(error, 'Retrieving Mail');
                return of(null);
            })
        )
    }

    // Cloudflare Email Address Obfuscation replaces emails in HTML with XOR-encoded spans.
    // The CF decoder script doesn't run in dynamically-created popup windows, so we decode here.
    private decodeCfEmails(html: string): string {
        const decode = (encoded: string) => {
            const r = parseInt(encoded.substring(0, 2), 16);
            let email = '';
            for (let i = 2; i < encoded.length; i += 2) {
                email += String.fromCharCode(parseInt(encoded.substring(i, 2), 16) ^ r);
            }
            return email;
        };
        // Replace <span class="__cf_email__"> and <a class="__cf_email__"> with decoded text
        html = html.replace(/<(span|a)[^>]+data-cfemail="([^"]+)"[^>]*>[\s\S]*?<\/\1>/g,
            (_, _tag, encoded) => decode(encoded));
        // Remove the now-useless cdn-cgi wrapper anchors
        html = html.replace(/<a[^>]+href="\/cdn-cgi\/l\/email-protection[^"]*"[^>]*>([\s\S]*?)<\/a>/g, '$1');
        return html;
    }

    showMail(mi: MailItem, dataSvc: AbstractHttpService, gridType: GridType) {
        this.getMail(mi, dataSvc, gridType).subscribe(htmlResponse => {
            if (htmlResponse) {
                const width = screen.availWidth * .8;
                const height = screen.availHeight * .8;
                // Dual monitors needs to push out...
                const top = window.screenTop + (screen.availHeight * .1);
                const left = window.screenLeft + (screen.availWidth * .1);
                const title = "Message Sent To " + mi.toAddress + " : " + mi.subject;
                const baseOptions = "toolbar=no,location=no,directories=no,status=no,menubar=no,scrollbars=yes,resizable=yes";
                const options = `${baseOptions},width=${width},top=${top},left=${left},height=${height}`
                const win = window.open("", title, options);
                const when = new Date(mi.createdAt * 1000).toLocaleDateString('en-IE', { weekday: "long", month: "long", day: "numeric" });
                const time = new Date(mi.createdAt * 1000).toLocaleTimeString('en-IE')
                const from = mi.fromAddress;
                const to = mi.toAddress;
                const body = this.decodeCfEmails(htmlResponse.body);
                const document = `<html>
                    <div>
                        <hr/>
                        <div style="margin-left: 20px; margin-right: 20px; line-height: 1.2em">
                            <p style="margin-bottom: 0px"><b>From</b>: ${from}</p>
                            <p style="margin-bottom: 0px"><b>To</b>: ${to}</p>
                            <p style="margin-bottom: 0px"><b>Sent</b>: ${when} @ ${time}</p>
                            <p style="margin-bottom: 0px"><b>Subject</b>: ${mi.subject}</p>
                        </div>
                        <hr/>
                    ${body}
                    </div>
                </html>`
                win.document.title = title;
                win.document.body.innerHTML = document;
            }
        });
    }


}
